/**
 * Apply for Certification Reimbursement — the modal behind the button on Page 1.
 *
 * ── Why a modal and not a jump to the expense form ────────────────────────────────────────
 *
 * Sending somebody to /expenses/new with the category pre-selected worked, and it was the wrong
 * shape: they left the page they were on, landed in a general expense form that mostly asks
 * about things certifications do not have, and came back to a list that had not obviously
 * changed. Applying is a small, specific act and it belongs where it was started, in the same
 * dialog as Add Certification so the two read as one module.
 *
 * ── It writes TWO records, to two tables ──────────────────────────────────────────────────
 *
 * A certification (what the employee is qualified in) and a claim (what they are asking to be
 * paid for it). Neither is an Expense — certification reimbursement has its own table and its
 * own approval fields, so the flow can change without any of it having to be safe for travel
 * claims. See types/certificationReimbursement.ts.
 *
 * ── Order of writes ───────────────────────────────────────────────────────────────────────
 *
 * The certification record is written FIRST, then the expense that references it. An expense
 * always points at a certification that exists; the reverse failure leaves a certification with
 * no claim attached, which is a harmless row in the employee's own history. The other order
 * would put an application with no details into a manager's queue.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canonicalizeEmail, findByIdentityWithManager, sameIdentity } from '../../services/identity';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { loadAllUsers, SimpleUser } from '../../services/userLoader';
import { UserRole } from '../../types/user';
import {
  CertificationDraft,
  CertificationSource,
  validateCertificationDraft,
} from '../../types/certification';
import {
  CertificationService,
  CertificationsNotDeployedError,
  describeCertificationError,
} from '../../services/certificationService';
import { CertificationReimbursementService } from '../../services/certificationReimbursementService';
import CertificationForm from './CertificationForm';
import './Certifications.css';

interface Props {
  onClose: () => void;
  onSubmitted: () => void;
}

const ApplyReimbursementModal: React.FC<Props> = ({ onClose, onSubmitted }) => {
  const { user } = useAuth();
  /* Always the signed-in employee — you claim your own certification, never somebody else's.
     Same rule as Add Certification, and the resolver fixes the owner from the token regardless. */
  const myEmail = canonicalizeEmail(user?.email);

  const [draft, setDraft] = useState<Partial<CertificationDraft>>({});
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const [errors, setErrors] = useState<string[]>([]);
  const [failure, setFailure] = useState('');
  const [saving, setSaving] = useState(false);

  const ownerEmail = myEmail;

  /*
   * Who this will go to, resolved BEFORE it is submitted.
   *
   * The reporting line was already looked up on submit, to route the claim. Doing it on open as
   * well is what lets the form say who will see it — and that is worth more than it sounds: an
   * approver the employee does not recognise is the moment to get the reporting line corrected,
   * and it is far cheaper to notice here than after a week of silence. The Regularization page
   * makes the same argument for the same reason.
   */
  const [approver, setApprover] = useState<{
    email?: string;
    name?: string;
    /** How the claim is routed, which is not always "to my manager". */
    route: 'manager' | 'admin-direct' | 'resolving';
  }>({ route: 'resolving' });

  useEffect(() => {
    let active = true;

    (async () => {
      let managerEmail = (user as any)?.managerEmail as string | undefined;

      if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
        try {
          const response = await CognitoAdminService.listUsers();
          managerEmail = findByIdentityWithManager(response.users, user.email)?.managerEmail;
        } catch {
          // Non-fatal: the form still submits, and the routing note below says what it knows.
        }
      }

      /*
       * A MANAGER's own claim skips manager approval and goes straight to an administrator —
       * the workflow bypasses it rather than asking somebody to approve themselves. Saying so
       * here stops it reading as a missing step.
       */
      const bypasses = user?.role === UserRole.MANAGER || !managerEmail;
      if (!active) return;

      if (bypasses) {
        setApprover({ route: 'admin-direct' });
        return;
      }

      let name: string | undefined;
      try {
        const directory: SimpleUser[] = await loadAllUsers();
        const match = directory.find((u) => sameIdentity(u.email, managerEmail));
        name = match?.fullName || match?.name;
      } catch {
        // The address alone is still useful; a missing display name is not worth failing over.
      }
      if (active) setApprover({ email: managerEmail, name, route: 'manager' });
    })();

    return () => {
      active = false;
    };
  }, [user]);

  /**
   * What is wrong, gathered from both halves at once.
   *
   * The certification rules come from the shared validator — the same one All Certifications
   * uses, so the mandatory-document rule cannot be looser on this form. The reimbursement rules
   * are only the two fields this modal adds.
   */
  const validate = (): string[] => {
    const problems = validateCertificationDraft(draft as CertificationDraft);
    const value = Number(amount);
    if (!amount.trim() || !Number.isFinite(value) || value <= 0) {
      problems.push('Enter the amount you are claiming.');
    }
    return problems;
  };

  const submit = async () => {
    setFailure('');
    const problems = validate();
    if (problems.length > 0) {
      setErrors(problems);
      return;
    }
    setErrors([]);
    setSaving(true);

    try {
      /*
       * The certification record first, then the claim that references it.
       *
       * Order matters and this is the safer of the two: a claim always points at a certification
       * that exists. Creating the claim first would leave an application in a manager's queue
       * referencing nothing, with no details to decide on. The reverse failure leaves a
       * certification with no claim — a harmless row in the employee's own history that they can
       * see on All Certifications and apply from again.
       */
      const certification = await CertificationService.create(
        draft as CertificationDraft,
        { email: myEmail, name: user?.name, employeeId: (user as any)?.employeeId },
        { source: CertificationSource.REIMBURSEMENT }
      );

      /* Already resolved when the form opened, and shown to the employee — so the claim goes
         to the approver the form named, not to a second lookup that might disagree. */
      await CertificationReimbursementService.create(
        {
          certificationId: certification.id,
          certificationName: draft.name!,
          provider: draft.provider!,
          examDate: draft.examDate,
          resultDate: draft.resultDate!,
          result: draft.result!,
          reimbursementAmount: Number(amount),
          currency: 'INR',
          documentKey: draft.documentKey,
          documentName: draft.documentName,
          notes,
        },
        {
          email: myEmail,
          name: user?.name,
          employeeId: (user as any)?.employeeId,
          managerEmail: approver.email,
        }
      );

      onSubmitted();
    } catch (error: any) {
      /*
       * The certification may already exist by the time this fires — it is written first.
       *
       * Said plainly rather than swallowed: the employee's record is not lost, and telling them
       * that stops them re-entering the whole thing and creating a duplicate. The undeployed-API
       * case is named separately, because "try again from All Certifications" is useless advice
       * when the whole module has no backend yet — that page shows the same notice.
       *
       * Branching on the ERROR TYPE, not on words in the message. Matching /Terraform|not
       * deployed/ against display copy meant an edit to that sentence silently re-routed the
       * branch, and it is the kind of break no test catches because both strings render fine.
       */
      const described = describeCertificationError(error, 'The application could not be submitted.');
      setFailure(
        error instanceof CertificationsNotDeployedError
          ? described
          : `${described} If the certification was already recorded, it is in All Certifications — ` +
            'apply again from there rather than re-entering it.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cert-modal" role="dialog" aria-modal="true" aria-label="Apply for certification reimbursement">
      <div className="cert-modal__panel">
        <div className="cert-modal__head">
          <h2>Apply for Certification Reimbursement</h2>
          <button type="button" className="cert-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="cert-modal__body">
          <p className="cert-modal__lead">
            A failed exam can still be claimed — the exam result and the decision on your claim
            are separate things.
          </p>

          {/*
            * Who decides, before anything is filled in.
            *
            * Named from the reporting line in Employee Master. An employee cannot choose their
            * approver, so the only useful thing the form can do is tell them which one they
            * have — and an address they do not recognise is a reporting line to get fixed.
            */}
          <div className="cert-approver">
            <span className="cert-form__label">This application goes to</span>
            {approver.route === 'resolving' ? (
              <p className="cert-approver__who cert-approver__who--muted">
                Finding your approver…
              </p>
            ) : approver.route === 'manager' ? (
              <p className="cert-approver__who">
                {approver.name || 'Your reporting manager'}
                <span className="cert-approver__email">{approver.email}</span>
                <span className="cert-approver__then">
                  Then to an administrator for the final decision.
                </span>
              </p>
            ) : (
              <p className="cert-approver__who">
                An administrator
                <span className="cert-approver__then">
                  {user?.role === UserRole.MANAGER
                    ? 'Manager approval is skipped for your own claims, so this goes straight to the admin step.'
                    : 'No reporting manager is set for you in Employee Master, so this goes straight to the admin step. Ask HR to set your manager if that looks wrong.'}
                </span>
              </p>
            )}
          </div>

          <CertificationForm
            value={draft}
            onChange={setDraft}
            ownerEmail={ownerEmail}
            disabled={saving}
          />

          <div className="cert-form__row">
            <div className="cert-form__field">
              <label htmlFor="reimb-amount">Reimbursement amount *</label>
              <input
                id="reimb-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                disabled={saving}
                placeholder="0.00"
                onChange={(e) => setAmount(e.target.value)}
              />
              <small className="cert-form__hint">What you paid for the certification, in ₹.</small>
            </div>
            <div className="cert-form__field">
              <label htmlFor="reimb-notes">Notes for your approver</label>
              <input
                id="reimb-notes"
                type="text"
                value={notes}
                disabled={saving}
                placeholder="Anything the approver should know"
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {errors.length > 0 && (
            <ul className="cert-form__errors" role="alert">
              {errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
          {failure && (
            <div className="certs__notice certs__notice--error" role="alert">{failure}</div>
          )}
        </div>

        <div className="cert-modal__actions">
          <button type="button" className="certs__btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="certs__btn certs__btn--primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyReimbursementModal;
